// Panels' templates, control.<id> (contract §8.3): a panel's page. A control
// in CONTROL_TEMPLATE_PORTS, the controls found on Gallivant's boards (W0),
// has a static port drawn as renderControl draws it; every other control is
// exactly the panel's own page (the cloud's pageFromPanel): one `<tc-face>`
// with its roles mapped to readings named from their paths and its look, no
// CSS. A face is today's drawing by construction (Q13).

import { getDefinition } from "../registry.js";
import { readingNames, reading, sortKeysDeep } from "./kit.js";
import { controlPort } from "./control-ports.js";

/** The controls with a static port (§8.3): the ones on Gallivant's boards. */
export const CONTROL_TEMPLATE_PORTS = Object.freeze([
  "arc-gauge",
  "history",
  "wind-rose",
]);

/** pageFromPanel's quoting of a look in a single-quoted attribute. */
const singleQuoted = (text) =>
  text.replace(/&/g, "&amp;").replace(/'/g, "&#39;");

/**
 * The panel's roles as readings, in its definition's role order and then any
 * other bound role, a path bound twice named once (pageFromPanel).
 */
export function controlReadings(spec) {
  const bindings =
    spec.bindings && typeof spec.bindings === "object" ? spec.bindings : {};
  let roles = [];
  try {
    roles = Object.keys(getDefinition(spec.control).roles);
  } catch {
    roles = [];
  }
  const ordered = [
    ...roles.filter((r) => typeof bindings[r] === "string"),
    ...Object.keys(bindings).filter(
      (r) => !roles.includes(r) && typeof bindings[r] === "string",
    ),
  ];
  const byPath = readingNames(ordered.map((r) => bindings[r]));
  const readings = {};
  const faceReadings = {};
  for (const role of ordered) {
    const name = byPath.get(bindings[role]);
    readings[name] = reading(bindings[role]);
    faceReadings[role] = name;
  }
  return { readings, faceReadings };
}

/** The `<tc-face>` page of a panel: pageFromPanel's, byte for byte. */
export function facePage(spec, look) {
  const { readings, faceReadings } = controlReadings(spec);
  const sparse = sortKeysDeep(look ?? {});
  const pairs = Object.entries(faceReadings).map(
    ([role, name]) => `${role}:${name}`,
  );
  const readingsAttribute =
    pairs.length > 0 ? ` readings="${pairs.join(" ")}"` : "";
  const lookAttribute =
    Object.keys(sparse).length > 0
      ? ` look='${singleQuoted(JSON.stringify(sparse))}'`
      : "";
  return {
    html: `<tc-face control="${spec.control}"${readingsAttribute}${lookAttribute}></tc-face>`,
    css: "",
    readings,
  };
}

/** control.<id>: a panel's page (§8.3). */
export function controlTemplate(source, context) {
  const spec = source.spec;
  const look = context?.control?.look ?? {};
  const port = CONTROL_TEMPLATE_PORTS.includes(spec.control)
    ? controlPort(spec, look, context)
    : null;
  const page = port ?? { ...facePage(spec, look), bare: true };
  return {
    id: `control.${spec.control}`,
    version: 1,
    html: page.html,
    css: page.css,
    readings: page.readings,
    live: [],
    feeds: [],
    card: { kind: "panel", icon: null },
    bare: page.bare === true,
  };
}
